<!-- Welcome screen: choose a data folder or the demo. Data stays on the user's computer. -->
<script lang="ts">
  import { onMount } from "svelte";
  import { demoSource, showWelcome, startBrowser } from "../lib/actions";
  import { app } from "../lib/state.svelte";

  type Fsx = typeof import("../data/fsx");
  let L = $state.raw<Fsx | null>(null);
  let saved = $state.raw<FileSystemDirectoryHandle | null>(null);
  let ready = $state(false);
  const canFs = $derived(!!L && L.supportsFolderAccess());

  onMount(async () => {
    L = await import("../data/fsx");
    saved = L.supportsFolderAccess() ? ((await L.loadHandle()) as FileSystemDirectoryHandle | null) : null;
    ready = true;
  });

  async function go(fn: () => Promise<void>) {
    try {
      await fn();
    } catch (e) {
      if (e && (e as Error).name === "AbortError") return;
      console.error(e);
      showWelcome(`Nie udało się otworzyć folderu: ${(e as Error).message}`);
    }
  }
  const reopen = () => go(async () => {
    if (!(await L!.ensurePermission(saved!, true))) throw new Error("brak zgody na dostęp do folderu");
    await startBrowser({ kind: "disk", handle: saved! });
  });
  const pick = () => go(async () => startBrowser({ kind: "disk", handle: await L!.pickFolder() }));
  // Safari/Firefox: folder loaded into memory on this thread (file paths don't carry over to the worker)
  const openDir = (files: FileList | null) => go(async () => {
    const fs = await L!.fileListFs(files ?? []);
    await startBrowser({ kind: "mem", files: fs.dump(), name: fs.name, readonly: true });
  });
  const demo = () => go(() => startBrowser(demoSource()));
</script>

{#if ready}
  <div class="welcome-box">
    <div class="welcome-head"><span class="logo big"></span><div><h1>Portfel</h1><p class="lead">Wyniki inwestycji w czasie, podatek Belki, obligacje skarbowe, inflacja i prognozy — bez wysyłania Twoich danych gdziekolwiek.</p></div></div>
    {#if app.welcomeError}<div class="notice">{app.welcomeError}</div>{/if}
    <div class="card">
      <h2>Twoje dane zostają na Twoim komputerze</h2>
      <ul class="welcome-list">
        <li>Wskazujesz folder na dysku, a przeglądarka czyta i zapisuje w nim pliki bezpośrednio. Transakcje, kwoty i numery rachunków <b>nie trafiają na żaden serwer</b>.</li>
        <li>Z internetu pobierane są tylko dane publiczne: notowania (Yahoo, Stooq), kursy NBP, inflacja GUS i parametry obligacji z obligacjeskarbowe.pl. Pośrednik tej strony widzi jedynie symbole papierów, o które pytamy.</li>
        <li>Folder to zwykłe pliki CSV i JSON — możesz je archiwizować, przenosić i otwierać w arkuszu. Aplikację można też uruchomić u siebie (<code>npm start</code>) na tym samym folderze.</li>
      </ul>
      <div class="row welcome-actions">
        {#if canFs && saved}
          <button class="btn primary" id="wReopen" onclick={reopen}>Otwórz folder „{saved.name}”</button><button class="btn" id="wPick" onclick={pick}>Wybierz inny folder</button>
        {:else if canFs}
          <button class="btn primary" id="wPick" onclick={pick}>Wybierz folder z danymi</button>
        {:else}
          <label class="btn primary" for="wDirInp">Otwórz folder (tylko odczyt)</label><input type="file" id="wDirInp" webkitdirectory multiple hidden onchange={(e) => openDir(e.currentTarget.files)} />
        {/if}
        <button class="btn" id="wDemo" onclick={demo}>Zobacz demo</button>
      </div>
      <p class="small muted">{canFs
        ? "Nowy portfel? Wybierz pusty folder i zaimportuj pliki z XTB lub IBKR — aplikacja założy w nim potrzebną strukturę. Przeglądarka zapamięta wybór; przy kolejnej wizycie wystarczy jedno kliknięcie, żeby znów dać jej dostęp."
        : "Ta przeglądarka nie pozwala stronom zapisywać plików w folderze — dane można przeglądać, ale importy i ustawienia nie zostaną zapisane. Pełną wersję obsługują Chrome, Edge i inne przeglądarki oparte na Chromium."}</p>
    </div>
  </div>
{/if}
